import React, {useEffect, useState, useCallback} from "react";
import {Fancybox} from "@fancyapps/ui";
import {PhotoModel} from "../types/protocols";
import {useFirebaseImage} from "../Helpers/ImageLoader";
import ClickableImage from "./ClickableImage";

// Individual photo component that loads its own URL and reports it back
const PhotoWithUrl: React.FC<{ 
    photo: PhotoModel, 
    buildingKey: string, 
    index: number,
    onImageClick: (photo: PhotoModel, url: string, index: number) => void,
    onUrlLoaded: (index: number, url: string) => void
}> = ({ photo, buildingKey, index, onImageClick, onUrlLoaded }) => {
    const { src } = useFirebaseImage(photo.storagePath);
    
    // Report the loaded URL back to parent
    useEffect(() => {
        if (src) {
            onUrlLoaded(index, src);
        }
    }, [src, index, onUrlLoaded]);
    
    return (
        <ClickableImage 
            photo={photo} 
            buildingKey={buildingKey} 
            onImageClick={(clickedPhoto, url) => onImageClick(clickedPhoto, url, index)} 
        />
    );
};

// Photo gallery component that manages all URLs and enables FancyBox navigation
const PhotoGallery: React.FC<{ 
    photos: { [key: string]: PhotoModel }, 
    buildingKey: string,
    onImageClick?: (photo: PhotoModel, url: string, index: number) => void,
    onFancyboxOpen?: () => void,
    onFancyboxClose?: () => void
}> = ({ photos, buildingKey, onImageClick, onFancyboxOpen, onFancyboxClose }) => {
    const [photoUrls, setPhotoUrls] = useState<{ [index: number]: string }>({});
    const [allUrlsLoaded, setAllUrlsLoaded] = useState<boolean>(false);
    
    const handleUrlLoaded = useCallback((index: number, url: string) => {
        setPhotoUrls(prev => ({ ...prev, [index]: url }));
    }, []);
    
    const handleImageClick = useCallback((clickedPhoto: PhotoModel, url: string, index: number) => {
        if (onImageClick) {
            onImageClick(clickedPhoto, url, index);
            return;
        }
        
        // Create array of all photos with their URLs for FancyBox gallery
        const photoEntries = Object.values(photos);
        const allPhotos = photoEntries.map((photo, photoIndex) => ({
            src: photoUrls[photoIndex] || photo.storagePath, // Use loaded URL or fallback to storagePath
            caption: photo.date,
        }));
        
        // Notify parent that Fancybox is opening
        if (onFancyboxOpen) {
            onFancyboxOpen();
        }
        
        Fancybox.show(
            allPhotos,
            {
                animated: true,
                showClass: "fancybox-fadeIn",
                startIndex: index,
                on: {
                    close: () => {
                        // Notify parent that Fancybox is closing
                        if (onFancyboxClose) {
                            onFancyboxClose();
                        }
                    }
                }
            }
        );
    }, [photos, photoUrls, onImageClick, onFancyboxOpen, onFancyboxClose]);
    
    // Check if all URLs are loaded
    useEffect(() => {
        const photoCount = Object.keys(photos).length;
        const loadedCount = Object.keys(photoUrls).length;
        setAllUrlsLoaded(loadedCount === photoCount);
    }, [photoUrls, photos]);
    
    return (
        <div style={{display: 'flex', gap: ' 10px', flexWrap: 'wrap'}}>
            {Object.entries(photos).map(([key, photo], index) => (
                <PhotoWithUrl
                    key={key}
                    photo={photo}
                    buildingKey={buildingKey}
                    index={index}
                    onImageClick={handleImageClick}
                    onUrlLoaded={handleUrlLoaded}
                />
            ))}
        </div>
    );
};

export default PhotoGallery;
